// @ts-strict-ignore
import * as React from 'react';
import { AppComponentProps, BucketComponentProps } from '../../typescriptHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { FetchableEntityType, FetchablePaginatedEntityType } from '../../entityType';
import { shareLink, workflowsAndBranchesFromBuildDistroBucketRules } from '../../helpers';
import Dropdown from 'antd/lib/dropdown';
import {
    selectBuildDistributionBucketById,
    selectIntegrationForCapability,
    selectCommonNotificationFromBuildDistroNotificationId,
    selectPaginatedBucketBuildsLimitAndOffset,
    selectPaginatedSharedWithMeBuildsLimitAndOffset,
    selectSharedWithMeBuildsCount,
    selectSharedWithMeBuildIds,
    selectIsDemoInstance,
    selectShouldShowSimplifiedBdUIForUser,
    selectAuthCredentials,
    selectAppSettings,
    selectUserCanPerformAction,
} from '../../selectors/selectors';

import { RootState } from '../../reducers/reducers';
import { DesktopAndMobileHeader } from './DesktopAndMobileHeader';
import {
    BuildDistributionBucketDownloadMetricRequest,
    UpdateBuildDistroBucketNotificationSettingsRequest,
    tryFetchEntity,
    tryFetchPaginatedEntities,
} from '../../actions/entities';
import { globalMessage } from '../../utils/globalMessage';
import Space from 'antd/lib/space';
import Search from 'antd/lib/input/Search';
import { antGrey, runwayColors } from '../styles/globalStyles';
import {
    BellOutlined,
    BlockOutlined,
    TeamOutlined,
    ShareAltOutlined,
    SettingOutlined,
    TabletOutlined,
    ExclamationCircleFilled,
    CloudUploadOutlined,
    DeleteOutlined,
    ThunderboltOutlined,
    PlusOutlined,
    DownOutlined,
    BarChartOutlined,
} from '@ant-design/icons';
import {
    BuildBucketAvatar,
    BucketBuildsList,
    MembersTable,
    SharedWithMeBuildsList,
    BucketAccessIconTooltip,
    WorkflowAndBranchTag,
    BuildsList,
} from './CommonComponents';
import {
    CommonNotification,
    BucketMember,
    BuildDistroNotificationType,
    IntegrationCapability,
    BuildDistroEntityError,
    BucketType,
    BuildDistroBucketRuleType,
} from '../../types';
import Tabs from 'antd/lib/tabs';
import Text from 'antd/lib/typography/Text';
import List from 'antd/lib/list';
import Modal from 'antd/lib/modal';
import { AddEditBucketMembers } from './AddEditBucketMembers';
import CommonNotificationSettingsModal from '../sharedComponents/CommonNotificationSettingsModal';
import { BuildDistributionUpdatable, UserUpdatable } from '../../actions/updatable';
import {
    TRIGGER_BUILD_DISTRIBUTION_BUILD,
    TRY_ARCHIVE_BUILD_DISTRIBUTION_BUCKET,
    triggerBuildDistributionBuild,
    tryArchiveBuildDistributionBucket,
    tryUpdateBuildDistributionBucket,
    ARCHIVE_BUILD_DISTRIBUTION_BUILD,
} from '../../actions/buildDistribution';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
    createAppBuildDistroBucketRoute,
    createAppBuildsRoute,
    createBuildDistroBucketBuildRoute,
    createBuildDistroSharedWithMeBuildRoute,
    createUserSettingsRoute,
} from '../../helpers/routeHelpers';
import { UploadNewBuildModal } from './UploadNewBuildModal';
import { BuildDetailsDrawer } from './BuildDetailsDrawer';
import { bucketDescriptionForType } from '../../helpers/displayHelpers';
import * as AnalyticEvents from '../../utils/analytics';
import { trackBuildDistroViewEvent } from '../../actions/trackEvent';
import { CreateEditBuildBucketModal } from './CreateEditBuildBucketModal';
import { BuildDistroEntityErrorResult } from './EntityError';
import NotificationRow from '../common/NotificationRow';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { AutomationsComponent } from '../appSettings/Automations';
import Switch from 'antd/lib/switch';
import { tryUpdateShouldShowSimplifiedBdUI } from '@/actions/user';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { useIsMobile } from '@/mediaQueriesHelpers';
import { debounce } from 'lodash';
import { searchForBuildDistributionBuilds } from '@/api';
import { UserSettingsPageView } from '@/components/userSettings/UserSettingsComponent';
import DatePickerTimeRange from '../DatePickerTimeRange';
import dayjs from 'dayjs';
import { BucketDownloadMetricsContainer } from './BucketDownloadMetricsContainer';
import { Tooltip } from 'antd/lib';

export interface AddBucketMembersModalProps {
    open: boolean;
    onCancel: () => void;
    initialMembers: BucketMember[];
    excludedMembers: BucketMember[];
    appId: string;
    orgId: string;
    bucketId: string;
}

export const AddBucketMembersModal: React.FC<AddBucketMembersModalProps> = ({
    open,
    onCancel,
    initialMembers,
    excludedMembers,
    appId,
    orgId,
    bucketId,
}) => {
    const dispatch = useDispatch();

    const [newMembers, setNewMembers] = React.useState<BucketMember[]>(initialMembers);
    const isAddingBuildDistributionBucketMembers = useIsLoading(
        BuildDistributionUpdatable.AddBuildDistroBucketMembers,
        bucketId,
    );
    const addBuildDistroBucketMembersError = useError(BuildDistributionUpdatable.AddBuildDistroBucketMembers, bucketId);
    const addBuildDistroBucketMembersSuccess = useUpdated(
        BuildDistributionUpdatable.AddBuildDistroBucketMembers,
        bucketId,
    );

    React.useEffect(() => {
        if (addBuildDistroBucketMembersError) {
            globalMessage.error(addBuildDistroBucketMembersError);
        }
    }, [addBuildDistroBucketMembersError]);

    React.useEffect(() => {
        if (addBuildDistroBucketMembersSuccess) {
            globalMessage.success('New members added successfully!');
            onCancel();
        }
    }, [addBuildDistroBucketMembersSuccess]);

    const addMembers = (newMembers: BucketMember[]) => {
        dispatch(
            tryUpdateBuildDistributionBucket(BuildDistributionUpdatable.AddBuildDistroBucketMembers, {
                orgId,
                appId,
                bucketId,
                members: newMembers,
            }),
        );
    };

    return (
        <Modal
            open={open}
            title="Add bucket members"
            okText="Save"
            onOk={() => addMembers(newMembers)}
            okButtonProps={{ loading: isAddingBuildDistributionBucketMembers, disabled: newMembers.length === 0 }}
            onCancel={onCancel}
            destroyOnClose
        >
            <AddEditBucketMembers
                initialMembers={initialMembers}
                excludedMembers={excludedMembers}
                appId={appId}
                orgId={orgId}
                onMembersChange={(newMembers) => setNewMembers(newMembers)}
            />
        </Modal>
    );
};

interface BuildBucketNotificationsProps {
    isEnabled: boolean;
    settings: CommonNotification[];
    bucketId: string;
    appId: string;
    orgId: string;
    initialNotificationId?: BuildDistroNotificationType;
    onInitialNotificationIdConsumed?: () => void;
}

const BuildBucketNotifications: React.FC<BuildBucketNotificationsProps> = (props) => {
    const isLoading = useIsLoading(BuildDistributionUpdatable.ToggleBuildDistroBucketNotifications, props.bucketId);
    const isSuccessful = useUpdated(BuildDistributionUpdatable.ToggleBuildDistroBucketNotifications, props.bucketId);
    const toggleNotificationsError = useError(
        BuildDistributionUpdatable.ToggleBuildDistroBucketNotifications,
        props.bucketId,
    );
    const [enabled, setEnabled] = React.useState(props.isEnabled);
    const [showNotificationSettings, setShowNotificationSettings] = React.useState(false);
    const [notificationIdSelectedForSettings, setNotificationIdSelectedForSettings] =
        React.useState<BuildDistroNotificationType>(null);

    React.useEffect(() => {
        if (props.initialNotificationId) {
            setNotificationIdSelectedForSettings(props.initialNotificationId);
            setShowNotificationSettings(true);
            props.onInitialNotificationIdConsumed?.();
        }
    }, [props.initialNotificationId, props.onInitialNotificationIdConsumed]);

    React.useEffect(() => {
        if (toggleNotificationsError) {
            globalMessage.error(toggleNotificationsError);
            setEnabled(!enabled);
        }
    }, [toggleNotificationsError]);

    React.useEffect(() => {
        if (isSuccessful) {
            globalMessage.success(`Bucket notifications ${enabled ? 'enabled' : 'disabled'}`);
        }
    }, [isSuccessful]);

    // Not used currently
    // const onToggleNotifications = (enabled: boolean) => {
    //     setEnabled(enabled);
    //     dispatch(
    //         tryUpdateBuildDistributionBucket(BuildDistributionUpdatable.ToggleBuildDistroBucketNotifications, {
    //             enabled,
    //             bucketId: props.bucketId,
    //             appId: props.appId,
    //             orgId: props.orgId,
    //         }),
    //     );
    // };

    const onClickSettings = (notificationId: string) => {
        setNotificationIdSelectedForSettings(notificationId as BuildDistroNotificationType);
        setShowNotificationSettings(true);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text
                    style={{
                        color: antGrey.grey8,
                        fontSize: 14,
                    }}
                >
                    Enable notifications for this bucket
                </Text>
                <Switch
                    style={{ marginRight: '8px' }}
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                    loading={isLoading}
                    checked={enabled}
                    onChange={onToggleNotifications}
                />
            </div> */}
            <List
                loading={isLoading}
                dataSource={props.settings ?? []}
                split
                renderItem={(notification) => (
                    <List.Item>
                        <NotificationRow
                            actionType={RunwayUserAction.UABucketUpdateNotifications}
                            bucketId={props.bucketId}
                            notification={notification}
                            notificationsEnabled={enabled}
                            onClickSettings={onClickSettings}
                        />
                    </List.Item>
                )}
            />
            <Text>
                You can enable email notifications for any bucket-level notification from{' '}
                <Link to={createUserSettingsRoute(UserSettingsPageView.Email)}>
                    User settings &gt; Email notifications
                </Link>
                .
            </Text>
            {notificationIdSelectedForSettings && (
                <BuildDistroNotificationSettingsModal
                    appId={props.appId}
                    orgId={props.orgId}
                    onClose={() => setShowNotificationSettings(false)}
                    open={showNotificationSettings}
                    notificationId={notificationIdSelectedForSettings}
                    bucketId={props.bucketId}
                    onAfterClose={() => {
                        setNotificationIdSelectedForSettings(null);
                    }}
                />
            )}
        </div>
    );
};

interface BuildDistroNotificationSettingsModalProps extends AppComponentProps {
    bucketId: string;
    notificationId: BuildDistroNotificationType;
    onClose: () => void;
    onAfterClose: () => void;
    open: boolean;
}

const BuildDistroNotificationSettingsModal: React.FC<BuildDistroNotificationSettingsModalProps> = (props) => {
    const dispatch = useDispatch();

    const onSubmit = (values: any) => {
        const request: UpdateBuildDistroBucketNotificationSettingsRequest = {
            orgId: props.orgId,
            appId: props.appId,
            notificationId: props.notificationId,
            bucketId: props.bucketId,
            ...values,
        };
        dispatch(
            tryUpdateBuildDistributionBucket(
                BuildDistributionUpdatable.UpdateBuildDistroBucketNotificationSettings,
                request,
            ),
        );
    };

    const notification = useSelector((state: RootState) =>
        selectCommonNotificationFromBuildDistroNotificationId(state, props.appId, props.bucketId, props.notificationId),
    );

    return (
        <CommonNotificationSettingsModal
            onSubmit={onSubmit}
            actionType={BuildDistributionUpdatable.UpdateBuildDistroBucketNotificationSettings}
            notification={notification}
            {...props}
        />
    );
};

export enum BuildBucketDetailsTab {
    Builds = 'builds',
    Members = 'members',
    Notifications = 'notifications',
    Automations = 'automations',
    Metrics = 'metrics',
}

export const BucketDetailsTabTitle: React.FC<{
    title: string;
    isActive: boolean;
    disabled?: boolean;
    icon: typeof TeamOutlined;
}> = ({ title, isActive, disabled, icon: Icon }) => {
    const tabTitle = (
        <div>
            <Icon
                style={{
                    color: isActive ? runwayColors.info : antGrey.grey6,
                }}
            />
            <Text
                style={{
                    color: isActive ? antGrey.grey8 : antGrey.grey7,
                }}
            >
                {title}
            </Text>
        </div>
    );
    return disabled ?
            <Tooltip title="You do not have permission to access this page. Please contact your Admin.">
                {tabTitle}
            </Tooltip>
        :   tabTitle;
};

export const SharedBuildsDetails: React.FC<AppComponentProps> = (props) => {
    const { orgId, appId } = props;

    const location = useLocation();

    const buildId = new URLSearchParams(location.search).get('buildId');
    const bucketId = new URLSearchParams(location.search).get('bucketId');

    const dispatch = useDispatch();

    const buildIds = useSelector((state: RootState) => selectSharedWithMeBuildIds(state, appId));
    const [selectedBuildId, setSelectedBuildId] = React.useState(null);

    const [activeTab, setActiveTab] = React.useState(BuildBucketDetailsTab.Builds);

    const isLoadingBuilds = useIsLoading(FetchablePaginatedEntityType.BuildDistributionSharedWithMeBuilds, appId);
    const fetchBuildsError = useError(FetchablePaginatedEntityType.BuildDistributionSharedWithMeBuilds, appId);

    const { limit = 10, offset = 0 } = useSelector((state: RootState) => {
        return selectPaginatedSharedWithMeBuildsLimitAndOffset(state, appId);
    });

    const totalBuildsCount = useSelector((state: RootState) => selectSharedWithMeBuildsCount(state, appId));
    const hasNotFetchedAllBuilds = totalBuildsCount > buildIds.length;

    const loadMoreBuilds = React.useCallback(() => {
        dispatch(
            tryFetchPaginatedEntities(
                FetchablePaginatedEntityType.BuildDistributionSharedWithMeBuilds,
                {
                    orgId: props.orgId,
                    appId: props.appId,
                    limit,
                    offset,
                },
                limit,
                offset,
                appId,
            ),
        );
    }, [limit, offset]);

    React.useEffect(() => {
        if (buildId) {
            setSelectedBuildId(buildId);
        }
    }, [buildId]);

    React.useEffect(() => {
        if (appId && orgId && buildIds.length === 0) {
            loadMoreBuilds();
        }
    }, [appId, orgId]);

    React.useEffect(() => {
        if (fetchBuildsError) {
            globalMessage.error(fetchBuildsError);
        }
    }, [fetchBuildsError]);

    return (
        <DesktopAndMobileHeader
            orgId={orgId}
            appId={appId}
            title={'Shared with me'}
            topLeftComponent={(titleComponent) => (
                <Space direction="vertical">
                    <Space>
                        <BlockOutlined
                            style={{
                                color: antGrey.grey6,
                                fontSize: 16,
                            }}
                        />
                        {titleComponent}
                    </Space>
                    <Text type="secondary">
                        Builds that have been individually shared with you by members of your team.
                    </Text>
                </Space>
            )}
        >
            {(isMobile) => (
                <Tabs
                    type="card"
                    destroyInactiveTabPane
                    activeKey={activeTab}
                    onChange={(key) => setActiveTab(key as BuildBucketDetailsTab)}
                    items={[
                        {
                            key: BuildBucketDetailsTab.Builds,
                            label: (
                                <BucketDetailsTabTitle
                                    title={totalBuildsCount === undefined ? 'Builds' : `Builds (${totalBuildsCount})`}
                                    isActive={activeTab === BuildBucketDetailsTab.Builds}
                                    icon={TabletOutlined}
                                />
                            ),
                            children: (
                                <>
                                    <SharedWithMeBuildsList
                                        appId={appId}
                                        orgId={orgId}
                                        buildIds={buildIds}
                                        isMobile={isMobile}
                                        isLoading={isLoadingBuilds}
                                        destinationURLFactory={(selectedBuildId, bucketId) =>
                                            createBuildDistroSharedWithMeBuildRoute(
                                                orgId,
                                                appId,
                                                selectedBuildId,
                                                bucketId,
                                            )
                                        }
                                        onLoadMore={hasNotFetchedAllBuilds ? () => loadMoreBuilds() : null}
                                        showLoadMore={hasNotFetchedAllBuilds}
                                    />
                                    {selectedBuildId && (
                                        <BuildDetailsDrawer
                                            buildId={selectedBuildId}
                                            onCancel={() => {
                                                setSelectedBuildId(null);
                                                const url = new URL(window.location.href);
                                                const searchParams = new URLSearchParams(location.search);
                                                searchParams.delete('buildId');
                                                searchParams.delete('bucketId');
                                                url.search = searchParams.toString();
                                                window.history.pushState({}, '', url);
                                            }}
                                            appId={appId}
                                            orgId={orgId}
                                            bucketId={bucketId}
                                        />
                                    )}
                                </>
                            ),
                        },
                    ]}
                />
            )}
        </DesktopAndMobileHeader>
    );
};

export const BuildBucketDetails: React.FC<BucketComponentProps> = (props) => {
    const { orgId, appId, bucketId } = props;

    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
    const buildId = searchParams.get('buildId');
    const notificationId = searchParams.get('notificationId') as BuildDistroNotificationType;
    const initialTimeRangeInDays = 14;

    const [selectedBuildId, setSelectedBuildId] = React.useState(buildId);
    const [notificationIdFromQuery, setNotificationIdFromQuery] =
        React.useState<BuildDistroNotificationType>(notificationId);
    const clearNotificationIdFromQuery = React.useCallback(() => setNotificationIdFromQuery(null), []);
    const [isEditBucketModalOpen, setIsEditBucketModalOpen] = React.useState(false);

    const initialActiveTab = notificationId ? BuildBucketDetailsTab.Notifications : BuildBucketDetailsTab.Builds;
    const [activeTab, setActiveTab] = React.useState(initialActiveTab);
    const [isNewBuildModalOpen, setIsNewBuildModalOpen] = React.useState(false);

    const isLoadingBuildDistributionBucket = useIsLoading(FetchableEntityType.BuildDistributionBucket, bucketId);
    const isArchivingBucket = useIsLoading(TRY_ARCHIVE_BUILD_DISTRIBUTION_BUCKET, bucketId);
    const fetchBucketError = useError(FetchableEntityType.BuildDistributionBucket, bucketId) as BuildDistroEntityError;
    const [buildDistroEntityError, setBuildDistroEntityError] = React.useState<BuildDistroEntityError>(null);
    const archiveBucketError = useError(TRY_ARCHIVE_BUILD_DISTRIBUTION_BUCKET, bucketId);
    const archiveBucketSuccess = useUpdated(TRY_ARCHIVE_BUILD_DISTRIBUTION_BUCKET, bucketId);

    const isTriggeringNewBuild = useIsLoading(TRIGGER_BUILD_DISTRIBUTION_BUILD, bucketId);
    const triggerNewBuildError = useError(TRIGGER_BUILD_DISTRIBUTION_BUILD, bucketId);
    const triggerNewBuildSuccess = useUpdated(TRIGGER_BUILD_DISTRIBUTION_BUILD, bucketId);

    const isDemoInstance = useSelector((state: RootState) => selectIsDemoInstance(state, orgId));

    const bucket = useSelector((state: RootState) => selectBuildDistributionBucketById(state, bucketId));
    const appImageUrl = useSelector((state: RootState) => selectAppSettings(state, appId).appImageUrl);

    const [searchedForBuilds, setSearchedForBuilds] = React.useState([]);
    const searchRef = React.useRef(null);
    const [searchText, setSearchText] = React.useState('');
    const [isLoadingSearchBuilds, setIsLoadingSearchBuilds] = React.useState(false);

    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));

    const archiveBuildSuccess = useUpdated(ARCHIVE_BUILD_DISTRIBUTION_BUILD, selectedBuildId);
    const archiveBuildError = useError(ARCHIVE_BUILD_DISTRIBUTION_BUILD, selectedBuildId);
    const bucketRoute = createAppBuildDistroBucketRoute(orgId, appId, bucketId);

    const [bucketDownloadMetricFilters, setbucketDownloadMetricFilters] =
        React.useState<BuildDistributionBucketDownloadMetricRequest>({
            bucketId,
            appId,
            orgId,
            upperBound: dayjs().format('YYYY-MM-DD'),
            lowerBound: dayjs().subtract(initialTimeRangeInDays, 'days').format('YYYY-MM-DD'),
        });

    const onDateSelected = (values) => {
        const [start, end] = values['metricsDateRange'];
        const lowerBound = start.format('YYYY-MM-DD');
        const upperBound = end.format('YYYY-MM-DD');
        setbucketDownloadMetricFilters({
            bucketId,
            appId,
            orgId,
            upperBound,
            lowerBound,
        });
    };

    const isLoadingBucketDownloadMetrics = useIsLoading(
        FetchableEntityType.BuildDistributionBucketDownloadMetrics,
        bucketId,
    );
    const fetchBucketDownloadMetricsError = useError(
        FetchableEntityType.BuildDistributionBucketDownloadMetrics,
        bucketId,
    ) as BuildDistroEntityError;

    React.useEffect(() => {
        if (fetchBucketDownloadMetricsError) {
            if (!Object.values(BuildDistroEntityError).includes(fetchBucketDownloadMetricsError)) {
                globalMessage.error(fetchBucketDownloadMetricsError);
            } else {
                setBuildDistroEntityError(fetchBucketDownloadMetricsError);
            }
        }
    }, [fetchBucketDownloadMetricsError]);

    React.useEffect(() => {
        if (!notificationId) {
            return;
        }

        setNotificationIdFromQuery(notificationId);
        setActiveTab(BuildBucketDetailsTab.Notifications);
    }, [notificationId]);

    React.useEffect(() => {
        dispatch(
            tryFetchEntity(
                FetchableEntityType.BuildDistributionBucketDownloadMetrics,
                bucketDownloadMetricFilters,
                bucketId,
            ),
        );
    }, [bucketDownloadMetricFilters, bucketId]);

    React.useEffect(() => {
        if (archiveBuildSuccess) {
            globalMessage.success('Build archived!');
            setSelectedBuildId(null);
            history.replace(bucketRoute);
        }
    }, [archiveBuildSuccess]);

    React.useEffect(() => {
        if (archiveBuildError) {
            globalMessage.error(archiveBuildError);
        }
    }, [archiveBuildError]);

    const debouncedSearch = React.useCallback(
        debounce((value) => {
            setIsLoadingSearchBuilds(true);

            searchForBuildDistributionBuilds({ query: value, bucketId: bucket.id, appId, orgId }, authCredentials)
                .then((builds) => {
                    setSearchedForBuilds(builds);
                    setIsLoadingSearchBuilds(false);
                })
                .catch((error) => {
                    setSearchedForBuilds([]);
                    setIsLoadingSearchBuilds(false);
                    globalMessage.error(error.message);
                });
        }, 330),
        [authCredentials],
    );

    React.useEffect(() => {
        if (!searchText) {
            setSearchedForBuilds([]);
            return;
        }

        debouncedSearch(searchText);
    }, [searchText, appId, orgId]);

    const buildSystemIntegration = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, appId, IntegrationCapability.BuildSystem),
    );

    const vcsIntegration = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, appId, IntegrationCapability.VersionControl),
    );

    React.useEffect(() => {
        if (isDemoInstance) {
            return;
        }

        if (appId && orgId) {
            dispatch(
                tryFetchEntity(
                    FetchableEntityType.BuildDistributionBucket,
                    {
                        orgId: props.orgId,
                        appId: props.appId,
                        bucketId: props.bucketId,
                    },
                    bucketId,
                ),
            );
        }
    }, [appId, orgId, props.bucketId]);

    React.useEffect(() => {
        if (fetchBucketError) {
            if (!Object.values(BuildDistroEntityError).includes(fetchBucketError)) {
                globalMessage.error(fetchBucketError);
            } else {
                setBuildDistroEntityError(fetchBucketError);
            }
        } else {
            setBuildDistroEntityError(null);
        }
        if (triggerNewBuildError) {
            globalMessage.error(triggerNewBuildError);
        }
    }, [fetchBucketError, triggerNewBuildError, props.bucketId]);

    const isLoadingBuilds = useIsLoading(FetchablePaginatedEntityType.BuildDistributionBucketBuilds, bucketId);
    const fetchBuildsError = useError(FetchablePaginatedEntityType.BuildDistributionBucketBuilds, bucketId);

    React.useEffect(() => {
        if (archiveBucketError) {
            globalMessage.error(archiveBucketError);
        }
    }, [archiveBucketError]);

    const buildIds = bucket?.buildIds ?? [];

    const { limit = 20, offset = 0 } = useSelector((state: RootState) => {
        return selectPaginatedBucketBuildsLimitAndOffset(state, bucketId);
    });

    const hasNotFetchedAllBuilds = bucket?.totalBuildsCount > buildIds.length;

    const loadMoreBuilds = React.useCallback(() => {
        dispatch(
            tryFetchPaginatedEntities(
                FetchablePaginatedEntityType.BuildDistributionBucketBuilds,
                {
                    orgId: props.orgId,
                    appId: props.appId,
                    bucketId: props.bucketId,
                },
                limit,
                offset,
                bucketId,
            ),
        );
    }, [limit, offset, props.bucketId]);

    React.useEffect(() => {
        if (!isDemoInstance && appId && orgId && bucket && !bucket?.buildIds) {
            loadMoreBuilds();
        }
    }, [appId, orgId, bucket?.id]);

    const showArchiveConfirm = () => {
        Modal.confirm({
            title: 'Are you sure you want to archive this bucket?',
            icon: <ExclamationCircleFilled />,
            okText: 'Yes, archive bucket',
            okType: 'danger',
            cancelText: 'No',
            onOk: archiveRelease,
            maskClosable: true,
        });
    };

    const archiveRelease = () => {
        dispatch(
            tryArchiveBuildDistributionBucket({
                orgId: props.orgId,
                appId: props.appId,
                bucketId: props.bucketId,
            }),
        );
    };

    const triggerCIBuild = () => {
        dispatch(
            triggerBuildDistributionBuild({
                orgId,
                appId,
                bucketId,
            }),
        );
    };

    const handleBucketShare = (bucketUrl: string) => {
        if (isMobile) {
            shareLink(bucketUrl);
        } else {
            navigator.clipboard.writeText(bucketUrl);
            globalMessage.success('Bucket link copied to clipboard!');
        }

        dispatch(
            trackBuildDistroViewEvent({
                orgId,
                appId,
                bucketId: bucket?.id,
                eventName: AnalyticEvents.Event.BuildDistroClickShareBucketCta,
                extraProperties: {
                    context: 'bucketDetails',
                },
            }),
        );
    };

    const makePublicBucketLink = (bucketHash: string) => {
        return `${window.location.origin}/bucket/${bucketHash}`;
    };

    React.useEffect(() => {
        if (archiveBucketSuccess) {
            globalMessage.success(`Bucket archived!`);
            history.replace(createAppBuildsRoute(props.orgId, props.appId));
        }
        if (triggerNewBuildSuccess) {
            globalMessage.success(
                `Build triggered! It may take a few seconds for newly triggered builds to appear in the bucket.`,
            );
        }
    }, [archiveBucketSuccess, triggerNewBuildSuccess]);

    React.useEffect(() => {
        if (fetchBuildsError) {
            globalMessage.error(fetchBuildsError);
        }
    }, [fetchBuildsError]);

    React.useEffect(() => {
        dispatch(
            trackBuildDistroViewEvent({
                orgId,
                appId,
                bucketId,
                eventName: AnalyticEvents.Event.BuildDistroViewBucket,
            }),
        );
    }, [bucketId]);

    const bucketDesc = bucket ? bucketDescriptionForType(bucket.type) : null;

    const workflowsAndBranches = bucket ? workflowsAndBranchesFromBuildDistroBucketRules(bucket.rules) : [];

    const userId = useSelector((state: RootState) => state.user.id);
    const shouldShowSimplifiedBuildUI = useSelector((state: RootState) => selectShouldShowSimplifiedBdUIForUser(state));
    const isUpdatingSimplifiedBuildUISetting = useIsLoading(UserUpdatable.UPDATE_SHOW_SIMPLIFIED_BD_UI, userId);
    const isMobile = useIsMobile();

    const userCanPerformAction = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UABucketInstallBuilds, null, null, bucketId),
    );

    return (
        <BuildDistroEntityErrorResult errorType={buildDistroEntityError}>
            <DesktopAndMobileHeader
                orgId={orgId}
                appId={appId}
                title={bucket?.name ?? ''}
                isLoading={isLoadingBuildDistributionBucket}
                titleFontSize={20}
                topRightComponent={
                    <Space>
                        {bucket?.publicBucketLinkEnabled ?
                            <PermissionedActionButton
                                trigger={['hover']}
                                type="link"
                                buttonsRender={([_, rightButton]) => {
                                    // Custom approach to show a single, non-segmented dropdown button with PermissionedActionButton.
                                    return [
                                        null,
                                        React.cloneElement(rightButton as React.ReactElement<any, string>, {
                                            icon: <ShareAltOutlined />,
                                            children: (
                                                <Space>
                                                    Share bucket
                                                    <DownOutlined />
                                                </Space>
                                            ),
                                        }),
                                    ];
                                }}
                                shouldUseCustomButtonsRender
                                menu={{
                                    items: [
                                        {
                                            key: 'public-link',
                                            label: 'Copy public bucket link',
                                        },
                                        {
                                            key: 'internal-link',
                                            label: 'Copy internal bucket link',
                                        },
                                    ],
                                    onClick: (e) => {
                                        const linkType = e.key as 'internal-link' | 'public-link';

                                        let bucketUrl: string;
                                        if (linkType === 'internal-link') {
                                            const bucketRoute = createAppBuildDistroBucketRoute(orgId, appId, bucketId);
                                            bucketUrl = `${window.location.origin}${bucketRoute}`;
                                        } else {
                                            bucketUrl = makePublicBucketLink(bucket?.publicHash);
                                        }
                                        handleBucketShare(bucketUrl);
                                    },
                                }}
                            />
                        :   <PermissionedActionButton
                                icon={<ShareAltOutlined />}
                                type="link"
                                onClick={() => {
                                    const bucketRoute = createAppBuildDistroBucketRoute(orgId, appId, bucketId);
                                    const fullUrl = `${window.location.origin}${bucketRoute}`;
                                    handleBucketShare(fullUrl);
                                }}
                            >
                                Share bucket link
                            </PermissionedActionButton>
                        }
                        <Dropdown.Button
                            loading={isArchivingBucket}
                            menu={{
                                items: [
                                    {
                                        label: 'Archive bucket',
                                        key: '1',
                                        icon: <DeleteOutlined />,
                                        onClick: showArchiveConfirm,
                                        danger: true,
                                    },
                                ],
                            }}
                            onClick={() => {
                                setIsEditBucketModalOpen(true);
                            }}
                        >
                            <Space>
                                <SettingOutlined style={{ color: runwayColors.primary }} />
                                <Text>Edit bucket</Text>
                            </Space>
                        </Dropdown.Button>
                    </Space>
                }
                topLeftComponent={(titleComponent) => (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'start',
                            gap: 28,
                            marginTop: 8,
                        }}
                    >
                        {bucket && <BuildBucketAvatar appImageUrl={appImageUrl} size={54} bucket={bucket} />}
                        <Space
                            direction="vertical"
                            size={4}
                            style={{
                                marginTop: 4,
                            }}
                        >
                            <Space size={12}>
                                {titleComponent}
                                {bucket && (
                                    <BucketAccessIconTooltip
                                        isExternalBucketView={bucket.publicBucketLinkEnabled}
                                        bucketHasOrgWideAccess={bucket.orgWideAccessEnabled}
                                    />
                                )}
                            </Space>
                            {bucketDesc && <Text type="secondary">{bucketDesc}</Text>}
                        </Space>
                    </div>
                )}
            >
                {(isMobile) =>
                    bucket && (
                        <>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'start',
                                    flexWrap: 'wrap',
                                    gap: 10,
                                    padding: '10px 0',
                                }}
                            >
                                {workflowsAndBranches.map((workflowAndBranch, index) => (
                                    <WorkflowAndBranchTag
                                        key={index}
                                        workflowAndBranch={workflowAndBranch}
                                        buildSystemIntegrationType={buildSystemIntegration?.id}
                                        vcsIntegrationType={vcsIntegration?.id}
                                    />
                                ))}
                            </div>
                            <Tabs
                                type="card"
                                destroyInactiveTabPane
                                activeKey={activeTab}
                                onChange={(key) => setActiveTab(key as BuildBucketDetailsTab)}
                                tabBarExtraContent={
                                    activeTab === BuildBucketDetailsTab.Metrics && {
                                        right: (
                                            <DatePickerTimeRange
                                                onFinish={onDateSelected}
                                                isLoading={isLoadingBucketDownloadMetrics}
                                                allowAnyTimeRange={true}
                                                initialTimeRangeInDays={14}
                                            />
                                        ),
                                    }
                                }
                                items={[
                                    {
                                        key: BuildBucketDetailsTab.Builds,
                                        label: (
                                            <BucketDetailsTabTitle
                                                title={`Builds (${bucket?.totalBuildsCount ?? 0})`}
                                                isActive={activeTab === BuildBucketDetailsTab.Builds}
                                                icon={TabletOutlined}
                                            />
                                        ),
                                        children: (
                                            <>
                                                <Space
                                                    direction="vertical"
                                                    style={{
                                                        width: '100%',
                                                    }}
                                                    size="large"
                                                >
                                                    <div
                                                        style={{
                                                            width: '100%',
                                                            display: 'flex',
                                                            justifyContent: 'flex-end',
                                                            gap: 20,
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        {/* TODO: For when we support it */}
                                                        {/* <Button icon={<CodeOutlined />} type="link">
                                                        Upload CLI guide
                                                    </Button> */}
                                                        {!isMobile && !isDemoInstance && (
                                                            <Search
                                                                placeholder="Search builds"
                                                                allowClear
                                                                value={searchText}
                                                                onChange={(e) => {
                                                                    setSearchText(e.target.value);
                                                                }}
                                                                ref={searchRef}
                                                                style={{ width: 300 }}
                                                            />
                                                        )}
                                                        {!isMobile && (
                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                <p style={{ marginBottom: 0, marginRight: '8px' }}>
                                                                    Show Details
                                                                </p>
                                                                <Switch
                                                                    loading={isUpdatingSimplifiedBuildUISetting}
                                                                    checked={!shouldShowSimplifiedBuildUI}
                                                                    onChange={(enabled, _) =>
                                                                        dispatch(
                                                                            tryUpdateShouldShowSimplifiedBdUI({
                                                                                enabled: !enabled,
                                                                                userId,
                                                                                orgId,
                                                                            }),
                                                                        )
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        {bucket?.rules.length >= 1 ?
                                                            bucket?.rules[0].type ===
                                                                BuildDistroBucketRuleType.Branch && (
                                                                <PermissionedActionButton
                                                                    icon={<PlusOutlined />}
                                                                    type="primary"
                                                                    loading={isTriggeringNewBuild}
                                                                    onClick={() => triggerCIBuild()}
                                                                    disabled={bucket.rules[0].isTokenizedBranch}
                                                                >
                                                                    Run build workflow
                                                                </PermissionedActionButton>
                                                            )
                                                        :   <PermissionedActionButton
                                                                actionType={RunwayUserAction.UABucketUploadBuilds}
                                                                bucketId={bucket?.id}
                                                                icon={<CloudUploadOutlined />}
                                                                type="primary"
                                                                onClick={() => setIsNewBuildModalOpen(true)}
                                                            >
                                                                Upload a new build
                                                            </PermissionedActionButton>
                                                        }
                                                    </div>
                                                    {searchText && (
                                                        <BuildsList
                                                            appId={appId}
                                                            orgId={orgId}
                                                            builds={searchedForBuilds}
                                                            isLoading={isLoadingSearchBuilds}
                                                            isMobile={isMobile}
                                                            setSelectedBuildId={(buildId) => {
                                                                const url = createBuildDistroBucketBuildRoute(
                                                                    orgId,
                                                                    appId,
                                                                    bucketId,
                                                                    buildId,
                                                                );

                                                                history.push(url);
                                                                setSelectedBuildId(buildId);
                                                            }}
                                                        />
                                                    )}
                                                    {!searchText && (
                                                        <BucketBuildsList
                                                            appId={appId}
                                                            orgId={orgId}
                                                            buildIds={buildIds}
                                                            isMobile={isMobile}
                                                            isLoading={isLoadingBuilds}
                                                            onBuildSelection={(buildId) => {
                                                                setSelectedBuildId(buildId);
                                                            }}
                                                            destinationURLFactory={(selectedBuildId, bucketId) =>
                                                                createBuildDistroBucketBuildRoute(
                                                                    orgId,
                                                                    appId,
                                                                    bucketId,
                                                                    selectedBuildId,
                                                                )
                                                            }
                                                            onLoadMore={
                                                                hasNotFetchedAllBuilds ? () => loadMoreBuilds() : null
                                                            }
                                                            showLoadMore={hasNotFetchedAllBuilds}
                                                        />
                                                    )}
                                                </Space>
                                                <UploadNewBuildModal
                                                    open={isNewBuildModalOpen}
                                                    onCancel={() => setIsNewBuildModalOpen(false)}
                                                    bucketName={bucket?.name}
                                                    appId={appId}
                                                    orgId={orgId}
                                                    bucketId={bucketId}
                                                />
                                                {selectedBuildId && (
                                                    <BuildDetailsDrawer
                                                        buildId={selectedBuildId}
                                                        buildSystemIntegrationType={buildSystemIntegration?.id}
                                                        onCancel={() => {
                                                            setSelectedBuildId(null);
                                                        }}
                                                        appId={appId}
                                                        orgId={orgId}
                                                        bucketId={bucket?.id}
                                                        afterOpenChange={(open) => {
                                                            if (open) {
                                                                return;
                                                            }
                                                            const url = new URL(window.location.href);
                                                            const searchParams = new URLSearchParams(location.search);
                                                            searchParams.delete('buildId');
                                                            url.search = searchParams.toString();
                                                            window.history.pushState({}, '', url);
                                                        }}
                                                    />
                                                )}
                                            </>
                                        ),
                                    },
                                    {
                                        key: BuildBucketDetailsTab.Members,
                                        label: (
                                            <BucketDetailsTabTitle
                                                title={`Members (${bucket?.members?.length ?? 0})`}
                                                icon={TeamOutlined}
                                                isActive={activeTab === BuildBucketDetailsTab.Members}
                                            />
                                        ),
                                        children: (
                                            <MembersTable
                                                appId={appId}
                                                orgId={orgId}
                                                members={bucket?.members ?? []}
                                                bucketId={bucketId}
                                            />
                                        ),
                                    },
                                    {
                                        key: BuildBucketDetailsTab.Automations,
                                        label: (
                                            <BucketDetailsTabTitle
                                                title="Automations"
                                                isActive={activeTab === BuildBucketDetailsTab.Automations}
                                                icon={ThunderboltOutlined}
                                            />
                                        ),
                                        children: (
                                            <AutomationsComponent bucketId={bucket?.id} appId={appId} orgId={orgId} />
                                        ),
                                    },
                                    bucket?.id && {
                                        key: BuildBucketDetailsTab.Metrics,
                                        label: (
                                            <BucketDetailsTabTitle
                                                title="Metrics"
                                                isActive={activeTab === BuildBucketDetailsTab.Metrics}
                                                icon={BarChartOutlined}
                                                disabled={!userCanPerformAction}
                                            />
                                        ),
                                        children: (
                                            <BucketDownloadMetricsContainer
                                                bucketId={bucket?.id}
                                                appId={appId}
                                                orgId={orgId}
                                                isLoading={isLoadingBucketDownloadMetrics}
                                            />
                                        ),
                                        disabled: !userCanPerformAction,
                                    },
                                    ...(bucket?.type !== BucketType.Personal ?
                                        [
                                            {
                                                key: BuildBucketDetailsTab.Notifications,
                                                label: (
                                                    <BucketDetailsTabTitle
                                                        title="Notifications"
                                                        isActive={activeTab === BuildBucketDetailsTab.Notifications}
                                                        icon={BellOutlined}
                                                    />
                                                ),
                                                children: (
                                                    <BuildBucketNotifications
                                                        isEnabled={bucket?.notificationsEnabled ?? false}
                                                        settings={bucket?.notifications ?? []}
                                                        bucketId={bucket?.id}
                                                        appId={appId}
                                                        orgId={orgId}
                                                        initialNotificationId={notificationIdFromQuery}
                                                        onInitialNotificationIdConsumed={clearNotificationIdFromQuery}
                                                    />
                                                ),
                                            },
                                        ]
                                    :   []),
                                ]}
                            />

                            <CreateEditBuildBucketModal
                                bucket={bucket}
                                open={isEditBucketModalOpen}
                                onCancel={() => {
                                    setIsEditBucketModalOpen(false);
                                }}
                                appId={appId}
                                orgId={orgId}
                            />
                        </>
                    )
                }
            </DesktopAndMobileHeader>
        </BuildDistroEntityErrorResult>
    );
};
